import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { allManga } from '@/mocks/manga';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const manga = allManga.find((m) => m.slug === slug);
  const relatedManga = allManga
    .filter((m) => m.slug !== slug && m.categorySlug === manga?.categorySlug)
    .slice(0, 3);

  useEffect(() => {
    if (manga) {
      document.title = `${manga.title}｜${manga.category}成人漫画 — 漫境`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', `${manga.title} - ${manga.description.slice(0, 140)}`);
      }
    }
  }, [manga]);

  if (!manga) {
    return (
      <main className="min-h-screen pt-16 flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-950">作品未找到</h1>
          <p className="mt-2 text-foreground-500">抱歉，您要查看的作品不存在或已被移除。</p>
          <Link to="/" className="inline-block mt-6 px-6 py-2 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer">
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const articleSchema = {
    headline: manga.title,
    author: { '@type': 'Person', name: manga.author },
    datePublished: manga.publishDate,
    description: manga.description,
    articleBody: manga.contentHtml.replace(/<[^>]*>/g, '').slice(0, 500),
    publisher: {
      '@type': 'Organization',
      name: '漫境',
    },
  };

  return (
    <>
      <SchemaOrg type="Article" data={articleSchema} />
      <main className="min-h-screen pt-16">
        <article className="max-w-4xl mx-auto px-4 md:px-6 py-8">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: manga.category, href: `/category/${manga.categorySlug}` },
              { label: manga.title },
            ]}
          />

          <header className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xs font-medium text-primary-500 bg-primary-100 px-3 py-1 rounded-full">
                {manga.category}
              </span>
              <span className={`text-xs font-semibold px-3 py-1 rounded-full ${
                manga.status === '已完结'
                  ? 'bg-accent-100 text-accent-700'
                  : 'bg-secondary-100 text-secondary-700'
              }`}>
                {manga.status}
              </span>
            </div>
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight">
              {manga.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-foreground-500">
              <span>作者：<strong className="text-foreground-800">{manga.author}</strong></span>
              <span>更新：{manga.publishDate}</span>
              <span>共 <strong className="text-foreground-800">{manga.chapters}</strong> 话</span>
              <span className="flex items-center gap-1">
                <i className="ri-star-fill text-accent-500"></i>
                <strong className="text-foreground-800">{manga.rating}</strong>
                <span className="text-foreground-400">({manga.ratingCount}人评分)</span>
              </span>
            </div>
          </header>

          <div className="flex flex-col lg:flex-row gap-10">
            <div className="lg:w-64 flex-shrink-0">
              <div className="rounded-lg overflow-hidden shadow-sm">
                <img
                  src={manga.coverImage}
                  alt={manga.title}
                  title={`${manga.title} - ${manga.category}成人漫画`}
                  className="w-full object-cover object-top"
                />
              </div>
              <div className="mt-4 space-y-3">
                <div className="flex flex-wrap gap-1">
                  {manga.tags.map((tag) => (
                    <span key={tag} className="text-xs text-foreground-600 bg-background-100 px-2 py-1 rounded">
                      {tag}
                    </span>
                  ))}
                </div>
                <Link
                  to={`/category/${manga.categorySlug}`}
                  className="block w-full text-center px-4 py-2 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  浏览更多{manga.category}
                </Link>
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <div className="bg-background-100 rounded-lg p-6 md:p-8 mb-8">
                <h2 className="font-heading text-lg font-semibold text-foreground-950 mb-3">作品简介</h2>
                <p className="text-sm text-foreground-700 leading-relaxed">{manga.description}</p>
              </div>

              <div className="prose-custom">
                <h2 className="font-heading text-lg font-semibold text-foreground-950 mb-4">试读章节</h2>
                <div
                  className="text-sm text-foreground-700 leading-relaxed space-y-4"
                  dangerouslySetInnerHTML={{ __html: manga.contentHtml }}
                />
              </div>
            </div>
          </div>
        </article>

        {relatedManga.length > 0 && (
          <section className="max-w-4xl mx-auto px-4 md:px-6 py-10 border-t border-background-200/70">
            <h2 className="font-heading text-xl font-bold text-foreground-950 mb-6">相关推荐</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {relatedManga.map((m) => (
                <Link
                  key={m.id}
                  to={`/article/${m.slug}`}
                  className="group cursor-pointer"
                >
                  <div className="flex gap-4">
                    <div className="w-16 h-20 flex-shrink-0 rounded overflow-hidden">
                      <img
                        src={m.coverImage}
                        alt={m.title}
                        title={`${m.title} - 成人漫画推荐`}
                        className="w-full h-full object-cover object-top"
                        loading="lazy"
                      />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors">
                        {m.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-1">{m.author}</p>
                      <p className="text-xs text-accent-500 mt-1">{m.rating} ★</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
    </>
  );
}